import React from 'react'
import { KeywordManagerContext } from './keyword-manager-context'
import classnames from 'classnames'

import { KeywordManagerState } from './interfaces'
import { DownloadOutlined, DownOutlined, FolderOutlined } from '@ant-design/icons'
import { Button } from '@pushly/aqe/lib/components'
import { Button as AntDButton, Divider, Dropdown, Flex, Input, Menu, Space } from 'antd'

type ActionKey = keyof KeywordManagerState['actions']

interface IKMLHProps {
    title?: string
    hideSearch?: boolean
    filterSize?: 'small' | 'middle' | 'large'
    baseClassName?: string
    hideActions?: ActionKey | ActionKey[] | boolean
}

export const KeywordManagerListHeader = (props: IKMLHProps) => {
    const context: React.ContextType<typeof KeywordManagerContext> = React.useContext(KeywordManagerContext)
    let liveTypeTimeout

    const {
        onExportClick,
        onAddKeywordsClick,
        onSearchClick,
        onFilterChange,
        onHandleBulkMenuClick,
        filters,
        selectedKeywords,
    } = context

    const { title, hideSearch, hideActions, filterSize } = props

    const baseClassName = props.baseClassName ?? 'keyword-manager'

    const allActions: ActionKey[] = ['addKeywords', 'bulkKeywordActions', 'exportKeywords']

    let actionsHidden: ActionKey[] = []
    if (hideActions === true) {
        actionsHidden = Array.from(allActions)
    } else if (hideActions) {
        actionsHidden = Array.isArray(hideActions) ? hideActions : [hideActions]
    }

    const hideAllActions = actionsHidden.length === allActions.length
    const allBulkActions = ['delete']

    const enabled = selectedKeywords.length > 0

    return (
        <Flex justify="space-between" gap={8} className={classnames(`${baseClassName}-list-header`)}>
            <Flex>
                <span
                    className={classnames(`${baseClassName}-list-title`, {
                        hidden: !title,
                    })}
                >
                    {title}
                </span>

                <Input.Search
                    className={classnames(`${baseClassName}-list-search`)}
                    size={filterSize ?? 'middle'}
                    enterButton={true}
                    placeholder="Search ..."
                    defaultValue={filters.search}
                    onSearch={onSearchClick}
                    onChange={(ev) => {
                        clearTimeout(liveTypeTimeout)
                        const value = ev.target.value
                        liveTypeTimeout = setTimeout(() => {
                            onFilterChange('search', value)
                        }, 320)
                    }}
                    style={{ width: 300 }}
                />
            </Flex>

            <Space
                className={classnames(`${baseClassName}-list-actions`, {
                    hidden: hideAllActions,
                })}
            >
                {!actionsHidden.includes('addKeywords') && (
                    <AntDButton size="middle" type="primary" onClick={onAddKeywordsClick}>
                        <span>Add Keywords</span>
                    </AntDButton>
                )}

                {!actionsHidden.includes('bulkKeywordActions') && (
                    <Dropdown
                        disabled={!enabled}
                        trigger={['click']}
                        popupRender={() => (
                            <Menu onClick={onHandleBulkMenuClick}>
                                <Menu.Item key="action-archive" icon={<FolderOutlined />}>
                                    Archive
                                </Menu.Item>
                            </Menu>
                        )}
                    >
                        <AntDButton size="middle" type={enabled ? 'primary' : 'default'}>
                            <span>Bulk Actions</span> <DownOutlined />
                        </AntDButton>
                    </Dropdown>
                )}

                {!actionsHidden.includes('exportKeywords') && (
                    <>
                        <Divider className={`${baseClassName}-list-actions-divider`} type="vertical" />
                        <Button className={`${baseClassName}-list-actions-export`} key="export" onClick={onExportClick}>
                            <DownloadOutlined />
                            &nbsp;
                            <span>Export</span>
                        </Button>
                    </>
                )}
            </Space>
        </Flex>
    )
}
